// ─────────────────────────────────────────────────────────────────────────────
// useCheckout — initiates a Stripe Checkout Session
//
// Usage:
//   const { startCheckout, loading, error } = useCheckout()
//
//   // Foundation subscription
//   await startCheckout({ type: 'subscription_t1' })
//
//   // Branch unlock
//   await startCheckout({ type: 'branch_unlock', treeId: 'infrastructure' })
//
//   // Cert package
//   await startCheckout({ type: 'cert_package', certId: 'comptia_a' })
//
//   // Software package
//   await startCheckout({ type: 'software_package', packageId: 'web_design' })
//
// On success, Stripe redirects the user to the checkout page.
// After payment, Stripe redirects back to /dashboard?checkout=success
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback } from 'react'
import { useAuth } from './useAuth'
import { supabase } from '../lib/supabase'

export function useCheckout() {
  const { profile, user } = useAuth()
  const [loading, setLoading] = useState(false)
  const [error,   setError  ] = useState(null)

  const startCheckout = useCallback(async ({ type, treeId, certId, packageId }) => {
    if (!user?.id || !user?.email) {
      setError('You must be signed in to make a purchase.')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Get the current session JWT to authenticate the request
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('No active session. Please sign in again.')
      }

      const res = await fetch('/.netlify/functions/checkout', {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          type,
          userId:    user.id,
          email:     user.email,
          fullName:  profile?.full_name || '',
          treeId,
          certId,
          packageId,
        }),
      })

      const data = await res.json()

      if (!res.ok || !data.url) {
        throw new Error(data.error || 'Failed to create checkout session')
      }

      // Redirect to Stripe Checkout
      window.location.href = data.url

    } catch (err) {
      console.error('Checkout error:', err)
      setError(err.message || 'Something went wrong. Please try again.')
      setLoading(false)
    }

    // Don't setLoading(false) on success — page will redirect
  }, [user, profile])

  return { startCheckout, loading, error }
}
